import api from 'api'
import type { AxiosRequestConfig } from 'axios'

import { validateApiResponse } from 'utils/validateApiResponse'

import {
  getAssignableAssigneesForImportRequestResponseSchema,
  getImportRequestFilterAssigneesResponseSchema,
  getImportRequestFilterUsersResponseSchema,
  getImportRequestFilterWorkspacesResponseSchema,
  getImportRequestLabelsResponseSchema,
  getImportRequestsVerificationResponseSchema,
  parseImportRequestVerificationDetail
} from './schemas'
import type {
  BulkAssignImportRequestsParams,
  BulkUnassignImportRequestsParams,
  GetAssignableAssigneesForImportRequestResponse,
  GetImportRequestFilterAssigneesResponse,
  GetImportRequestFilterUsersResponse,
  GetImportRequestFilterWorkspacesResponse,
  GetImportRequestLabelsResponse,
  GetImportRequestsVerificationParams,
  GetImportRequestsVerificationResponse,
  ImportRequestVerificationDetail
} from './types'

// Response shapes follow airlock-be OpenAPI (`/v1/import-requests/*`). Mutations return empty bodies (201/204).

export const getImportRequestVerification = async (requestId: string): Promise<ImportRequestVerificationDetail> => {
  const { data } = await api.get<ImportRequestVerificationDetail>(`/import-requests/verification/${requestId}`)
  return parseImportRequestVerificationDetail(data)
}

/** Empty 204 response per OpenAPI. */
export const approveImportRequest = async (requestId: string): Promise<void> => {
  await api.put(`/import-requests/verification/${requestId}/approve`)
}

/** Empty 204 response per OpenAPI. */
export const rejectImportRequest = async (requestId: string, reason: string): Promise<void> => {
  await api.put(`/import-requests/verification/${requestId}/reject`, { reason })
}

export const getImportRequestLabels = async (options?: AxiosRequestConfig): Promise<GetImportRequestLabelsResponse> => {
  const { data } = await api.get<GetImportRequestLabelsResponse>('/import-requests/filters/labels', options)
  return validateApiResponse(getImportRequestLabelsResponseSchema, data)
}

/** Empty 201 response per OpenAPI. */
export const createImportRequestLabel = async (requestId: string, name: string): Promise<void> => {
  await api.post(`/import-requests/${requestId}/labels`, { name })
}

/** Empty 204 response per OpenAPI. */
export const deleteImportRequestLabel = async (requestId: string, labelId: string): Promise<void> => {
  await api.delete(`/import-requests/${requestId}/labels/${labelId}`)
}

export const getImportRequestsVerification = async (
  params: GetImportRequestsVerificationParams,
  options?: AxiosRequestConfig
): Promise<GetImportRequestsVerificationResponse> => {
  const { data } = await api.get<GetImportRequestsVerificationResponse>('/import-requests/verification', {
    params,
    ...options
  })

  return validateApiResponse(getImportRequestsVerificationResponseSchema, data)
}

export const getUsersFromImportRequests = async (
  options?: AxiosRequestConfig
): Promise<GetImportRequestFilterUsersResponse> => {
  const { data } = await api.get<GetImportRequestFilterUsersResponse>('/import-requests/filters/users', options)
  return validateApiResponse(getImportRequestFilterUsersResponseSchema, data)
}

export const getAssigneesFromImportRequests = async (
  options?: AxiosRequestConfig
): Promise<GetImportRequestFilterAssigneesResponse> => {
  const { data } = await api.get<GetImportRequestFilterAssigneesResponse>('/import-requests/filters/assignees', options)
  return validateApiResponse(getImportRequestFilterAssigneesResponseSchema, data)
}

export const getWorkspacesFromImportRequests = async (
  options?: AxiosRequestConfig
): Promise<GetImportRequestFilterWorkspacesResponse> => {
  const { data } = await api.get<GetImportRequestFilterWorkspacesResponse>(
    '/import-requests/filters/workspaces',
    options
  )
  return validateApiResponse(getImportRequestFilterWorkspacesResponseSchema, data)
}

/** Empty 204 response per OpenAPI. */
export const bulkAssignImportRequests = async (params: BulkAssignImportRequestsParams): Promise<void> => {
  await api.put('/import-requests/bulk-assign', params)
}

/** Empty 204 response per OpenAPI. */
export const bulkUnassignImportRequests = async (params: BulkUnassignImportRequestsParams): Promise<void> => {
  await api.put('/import-requests/bulk-unassign', params)
}

export const getAssignableAssigneesForImportRequest = async (
  requestId: string,
  options?: AxiosRequestConfig
): Promise<GetAssignableAssigneesForImportRequestResponse> => {
  const { data } = await api.get<GetAssignableAssigneesForImportRequestResponse>(
    `/import-requests/${requestId}/assignable-assignees`,
    options
  )
  return validateApiResponse(getAssignableAssigneesForImportRequestResponseSchema, data)
}
